import React, { useCallback, useEffect, useRef, useState } from 'react';
import classnames from 'classnames';
import scoreToColorCode from '@hydra/utils/scoreToColorCode';
import useAnimateScore from '@hydra/common/customHooks/useAnimateScore';
import Fireworks from '@hydra/common/components/Fireworks';

import CircularProgress from '@material-ui/core/CircularProgress';
import { makeStyles, useTheme } from '@material-ui/core/styles';

const useStyles = makeStyles(theme => ({
  scoreCircle: {
    position: 'relative',
    fontWeight: theme.typography.fontWeightSemibold
  },
  scoreCircleDefault: {
    width: '160px',
    height: '160px'
  },
  scoreCircleMedium: {
    width: '100px',
    height: '100px'
  },
  scoreCircleSmall: {
    width: '40px',
    height: '40px'
  },
  score: {
    position: 'absolute',
    left: '50%',
    top: '50%',
    pointerEvents: 'none',
    transform: 'translate(-50%, -50%)'
  },
  scoreDefault: {
    fontSize: '64px'
  },
  scoreMedium: {
    fontSize: '40px'
  },
  scoreSmall: {
    fontSize: '20px'
  },
  bgCircle: {
    left: 0,
    top: 0,
    position: 'absolute'
  },
  roundCircle: {
    strokeLinecap: 'round',
    transition: 'all 1s cubic-bezier(0.42, 0, 1, 1)'
  },
  background: {
    color: theme.palette.grey[100]
  },
  animatedScore: {
    display: 'block',
    transform: 'scale(0.1)',
    opacity: 0,
    transition: 'transform 1s cubic-bezier(0.175, 0.885, 0.32, 1.275)'
  },
  animatedScoreShow: {
    transform: 'scale(1)',
    opacity: 1
  }
}));
type ISize = 'small' | 'medium' | 'default';
type IVariant = 'static' | 'determinate' | 'indeterminate' | undefined;
const sizeToProps = (size: ISize): { variant: IVariant, thickness: number, size: number } => {
  switch (size) {
    case 'small': {
      return {
        variant: 'determinate',
        thickness: 2,
        size: 40
      };
    }
    case 'medium': {
      return {
        variant: 'determinate',
        thickness: 1.75,
        size: 100
      };
    }
    default: {
      return {
        variant: 'determinate',
        thickness: 1.5,
        size: 160
      };
    }
  }
};

interface IScoreCircleProps {
  score: number
  size?: ISize
  showProgress?: boolean
  animate?: boolean,
  fireworks?: boolean
}
export const ScoreCircle = ({
  score,
  size = 'default',
  showProgress = false,
  animate = false,
  fireworks = false
}: IScoreCircleProps) => {
  const classes = useStyles();
  const circleClasses = classnames(
    classes.scoreCircle,
    {
      [classes.scoreCircleDefault]: size === 'default',
      [classes.scoreCircleMedium]: size === 'medium',
      [classes.scoreCircleSmall]: size === 'small'
    }
  );
  const scoreClasses = classnames(
    classes.score,
    {
      [classes.scoreDefault]: size === 'default',
      [classes.scoreMedium]: size === 'medium',
      [classes.scoreSmall]: size === 'small'
    }
  );
  const value: number = showProgress ? score : 100;
  const roundedScore: number = Math.round(score);
  const color = scoreToColorCode(roundedScore);
  const circleRef = useRef<HTMLSpanElement | null>();
  const scoreRef = useRef<any>();
  const {
    animatedScore,
    circleAnimationComplete,
    scoreAnimationComplete
  } = useAnimateScore({
    score,
    animate,
    circleRef,
    scoreRef
  });
  const [showFireworks, setShowFireworks] = useState<boolean>(false);
  const theme = useTheme();
  const isDesktop = useCallback(() => window.matchMedia(`(min-width: ${theme.breakpoints.values.sm}px)`).matches, [theme.breakpoints.values.sm]);

  useEffect(() => {
    if (fireworks && scoreAnimationComplete && score >= 70 && isDesktop()) {
      const timeout = setTimeout(() => {
        setShowFireworks(true);
      }, 100); // give the animation a small delay
      return () => clearTimeout(timeout);
    }
  }, [scoreAnimationComplete, score, isDesktop, fireworks]);

  return (
    <>
      <div className={circleClasses}>
        <CircularProgress
          color='secondary'
          classes={{
            colorSecondary: classes.background
          }}
          value={100}
          className={classnames(
            classes.scoreCircle,
            classes.bgCircle
          )}
          {...sizeToProps(size)}
        />
        <CircularProgress
          style={{ color }}
          value={animate ? animatedScore : value}
          className={classes.scoreCircle}
          classes={{ circle: classes.roundCircle }}
          {...sizeToProps(size)}
          ref={circleRef}
        />
        {animate
          ? (
            <span className={scoreClasses} style={{ color }}>
              <span
                className={classnames(
                  classes.animatedScore,
                  circleAnimationComplete ? classes.animatedScoreShow : ''
                )}
                ref={scoreRef}
              >
                {roundedScore}
              </span>
              {showFireworks && <Fireworks circleRef={circleRef} />}
            </span>
            )
          : (
            <span className={scoreClasses} style={{ color }}>
              {roundedScore}
            </span>
            )}
      </div>
    </>
  );
};
export default ScoreCircle;
